import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../data/store';
import { fetchConfigurationsThunk } from '../../data/thunks/projectConfigurationThunk';
import { ProjectConfiguration } from '../../data/models/projectConfiguration';
import { useNavigate } from 'react-router-dom';
import {
	Box,
	Card,
	CardContent,
	Typography,
	Grid,
	Skeleton,
} from '@mui/material';

export default function ProjectTypesScreen() {
	const dispatch = useDispatch<AppDispatch>();
	const { configurations, loading } = useSelector(
		(state: RootState) => state.projectConfigurationState
	);

	const [searchTerm, setSearchTerm] = useState('');

	useEffect(() => {
		dispatch(fetchConfigurationsThunk({}));
	}, []);

	const filteredConfigurations = useMemo(() => {
		if (!searchTerm) return configurations;
		return configurations.filter(
			(config) =>
				config.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
				config.description
					.toLowerCase()
					.includes(searchTerm.toLowerCase())
		);
	}, [configurations, searchTerm]);

	return (
		<Box
			sx={{
				padding: 1,
				height: '100%',
				overflow: 'auto',
				position: 'relative',
				width: '100%',
			}}
		>
			<Box sx={{ position: 'relative', minHeight: '400px' }}>
				{loading ? (
					<SkeletonGrid />
				) : (
					<ConfigurationGrid
						configurations={filteredConfigurations}
					/>
				)}
			</Box>
		</Box>
	);
}

interface ConfigurationGridProps {
	configurations: ProjectConfiguration[];
}

const ConfigurationGrid = ({ configurations }: ConfigurationGridProps) => {
	const navigate = useNavigate();

	return (
		<Grid container spacing={3} sx={{ padding: '16px' }}>
			{configurations.map((config) => (
				<Grid item xs={12} sm={6} md={4} lg={3} key={config.id}>
					<ConfigurationCard
						configuration={config}
						onClick={() =>
							navigate(
								`/project-configuration/types/${config.id}`
							)
						}
					/>
				</Grid>
			))}
		</Grid>
	);
};

interface ConfigurationCardProps {
	configuration: ProjectConfiguration;
	onClick: () => void;
}

const ConfigurationCard = ({
	configuration,
	onClick,
}: ConfigurationCardProps) => {
	return (
		<Card
			sx={{
				cursor: 'pointer',
				height: '100%',
				transition:
					'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
				'&:hover': {
					transform: 'translateY(-4px)',
					boxShadow: 4,
				},
				borderRadius: 3,
				overflow: 'hidden',
			}}
			onClick={onClick}
		>
			<Box
				sx={{
					background:
						'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
					color: 'white',
					p: 1,
					pl: 2,
					position: 'relative',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography
						variant="h6"
						component="h3"
						sx={{
							fontWeight: 600,
							fontSize: '1.1rem',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							maxWidth: 'calc(100% - 40px)',
						}}
					>
						{configuration.name}
					</Typography>
					{/* <IconButton
						size="small"
						sx={{
							color: 'white',
							'&:hover': {
								backgroundColor: 'rgba(255, 255, 255, 0.1)',
							},
						}}
						onClick={(e) => {
							e.stopPropagation();
							// Could open info modal here
						}}
					>
						<InfoOutlinedIcon fontSize="small" />
					</IconButton> */}
				</Box>
			</Box>

			<CardContent sx={{ p: 2, flexGrow: 1 }}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
					<Typography
						variant="body2"
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							fontWeight: 500,
							color: 'text.secondary',
						}}
					>
						<span>{configuration.foldersLength} Folders</span>
						<span>
							{configuration.dataCategoryLength} Data Categories
						</span>
					</Typography>

					<Typography
						variant="body2"
						sx={{
							fontWeight: 500,
							color: 'text.secondary',
							textAlign: 'left',
						}}
					>
						{configuration.templatesLength} Templates
					</Typography>
				</Box>
			</CardContent>
		</Card>
	);
};

const SkeletonGrid = () => {
	// Show 8 skeleton cards to fill the space nicely
	const skeletonCards = Array.from({ length: 16 }, (_, index) => (
		<Grid item xs={12} sm={6} md={4} lg={3} key={index}>
			<SkeletonCard />
		</Grid>
	));

	return (
		<Grid container spacing={3} sx={{ padding: '16px' }}>
			{skeletonCards}
		</Grid>
	);
};

const SkeletonCard = () => {
	return (
		<Card
			sx={{
				height: '100%',
				width: '100%',
				borderRadius: 3,
				overflow: 'hidden',
			}}
		>
			{/* Header section with gradient background */}
			<Box
				sx={{
					background:
						'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
					color: 'white',
					p: 1,
					pl: 2,
					position: 'relative',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Skeleton
						variant="text"
						width="60%"
						height={28}
						sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }}
					/>
					<Skeleton
						variant="circular"
						width={24}
						height={24}
						sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }}
					/>
				</Box>
			</Box>

			{/* Content section */}
			<CardContent sx={{ p: 2, flexGrow: 1 }}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
						}}
					>
						<Skeleton variant="text" width="45%" height={20} />
						<Skeleton variant="text" width="45%" height={20} />
					</Box>
					<Skeleton variant="text" width="35%" height={20} />
				</Box>
			</CardContent>
		</Card>
	);
};
